import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Heart, ShoppingCart, X, Lock } from 'lucide-react';
import StarRating from '../components/StarRating';

const WishlistPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { products, loading, toggle } = useWishlist();
  const { addToCart, currency, formatPrice } = useCart();

  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login?redirect=/wishlist" replace />;
  }

  const getName = (p) => p?.translations?.[getUiLang(i18n)]?.name || p?.translations?.sk?.name || p?.sku;
  const getPrice = (p) => (currency === 'EUR' ? p.price_eur : p.price_usd);

  return (
    <div className="container mx-auto px-4 py-8" data-testid="wishlist-page">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="h-7 w-7 text-orange-500 fill-orange-500" />
        <h1 className="text-3xl font-bold">{t('wishlist.title')}</h1>
        <span className="text-gray-500">({products.length})</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg" data-testid="wishlist-empty">
          <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">{t('wishlist.empty')}</p>
          <Link
            to="/catalog"
            className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
          >
            {t('wishlist.browse_catalog')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="wishlist-grid">
          {products.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden relative"
              data-testid={`wishlist-card-${p.id}`}
            >
              <button
                onClick={() => toggle(p.id)}
                className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur rounded-full hover:bg-red-100 transition z-10"
                title={t('wishlist.remove')}
                data-testid={`wishlist-remove-${p.id}`}
              >
                <X className="h-4 w-4 text-red-500" />
              </button>
              <Link to={`/product/${p.id}`} className="block">
                <div className="aspect-square bg-gray-100 overflow-hidden">
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={getName(p)}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">No image</div>
                  )}
                </div>
              </Link>
              <div className="p-4">
                <Link to={`/product/${p.id}`}>
                  <h3 className="font-semibold mb-2 line-clamp-2 hover:text-orange-500 transition">{getName(p)}</h3>
                </Link>
                {p.rating_count > 0 && (
                  <StarRating value={p.rating_avg} size="h-3.5 w-3.5" count={p.rating_count} />
                )}
                <div className="flex items-center justify-between mt-3">
                  {isAuthenticated ? (
                    <span className="text-lg font-bold text-gray-900">{formatPrice(getPrice(p))}</span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <Lock className="h-4 w-4" />
                      {t('catalog.login_for_price')}
                    </span>
                  )}
                  {p.stock > 1 && (
                    <button
                      onClick={() => addToCart(p, 1)}
                      className="p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                      data-testid={`wishlist-add-cart-${p.id}`}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
